import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Edit2, Check, X, ChevronDown, ChevronUp, Dumbbell } from 'lucide-react';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

const DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const emptyExercise = () => ({ name: '', series: '', reps: '', rest: '', notes: '', exerciseType: 'musculação', estimatedCalories: '' });
const emptyProgramTreino = () => ({ weekday: 'Segunda', workoutTemplateId: '', workoutTemplateName: '', timeOfDay: 'Manhã', exercises: [] });

const CATEGORIES = [
  { value: 'fullbody', label: 'Full Body' },
  { value: 'halfbody', label: 'Half Body' },
  { value: 'abc', label: 'ABC' },
  { value: 'abcd', label: 'ABCD' },
  { value: 'custom', label: 'Personalizado' },
];

const LEVELS = [
  { value: 'beginner', label: 'Iniciante' },
  { value: 'intermediate', label: 'Intermediário' },
  { value: 'advanced', label: 'Avançado' },
];

const TIME_OPTIONS = ['Manhã', 'Tarde', 'Noite'];

export default function AdminBiblioteca() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [showAddProgram, setShowAddProgram] = useState(false);
  const [saving, setSaving] = useState(false);

  const [templateForm, setTemplateForm] = useState({
    name: '', category: 'custom', description: '', exercises: [emptyExercise()],
  });
  const [programForm, setProgramForm] = useState({
    name: '', description: '', level: 'beginner', days: [],
  });

  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editTemplateForm, setEditTemplateForm] = useState({});
  const [editingProgram, setEditingProgram] = useState(null);
  const [editProgramForm, setEditProgramForm] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [alertMsg, setAlertMsg] = useState('');

  const inputCls = 'bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none px-3 py-2 text-sm w-full transition-colors';
  const labelCls = 'block text-xs text-[#555] uppercase tracking-widest mb-1';

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadTemplates();
    loadPrograms();
  // eslint-disable-next-line
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/workout-templates`, { headers });
      setTemplates(res.data);
    } catch {}
  };

  const loadPrograms = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/workout-programs`, { headers });
      setPrograms(res.data);
    } catch {}
  };

  // ── Template helpers ──
  const setTplEx = (i, field, val) =>
    setTemplateForm(f => ({ ...f, exercises: f.exercises.map((e, j) => j === i ? { ...e, [field]: val } : e) }));
  const addTplEx = () => setTemplateForm(f => ({ ...f, exercises: [...f.exercises, emptyExercise()] }));
  const removeTplEx = i => setTemplateForm(f => ({ ...f, exercises: f.exercises.filter((_, j) => j !== i) }));

  const setEditTplEx = (i, field, val) =>
    setEditTemplateForm(f => ({ ...f, exercises: f.exercises.map((e, j) => j === i ? { ...e, [field]: val } : e) }));
  const addEditTplEx = () => setEditTemplateForm(f => ({ ...f, exercises: [...f.exercises, emptyExercise()] }));
  const removeEditTplEx = i => setEditTemplateForm(f => ({ ...f, exercises: f.exercises.filter((_, j) => j !== i) }));

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/workout-templates`, templateForm, { headers });
      setTemplates(prev => [res.data, ...prev]);
      setTemplateForm({ name: '', category: 'custom', description: '', exercises: [emptyExercise()] });
      setShowAddTemplate(false);
    } catch (err) { setAlertMsg(err.response?.data?.detail || 'Erro ao criar template.'); }
    finally { setSaving(false); }
  };

  const handleDeleteTemplate = (id) => {
    setConfirm({
      message: 'Remover este template?',
      onConfirm: async () => {
        try {
          await axios.delete(`${BACKEND_URL}/api/admin/workout-templates/${id}`, { headers });
          setTemplates(prev => prev.filter(t => t.id !== id));
        } catch { setAlertMsg('Erro ao remover.'); }
      },
    });
  };

  const startEditTemplate = (tpl) => {
    setEditingTemplate(tpl.id);
    setEditTemplateForm({ ...tpl, exercises: tpl.exercises ? [...tpl.exercises] : [emptyExercise()] });
    setExpandedTemplate(tpl.id);
  };

  const saveEditTemplate = async (id) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/workout-templates/${id}`, editTemplateForm, { headers });
      setTemplates(prev => prev.map(t => t.id === id ? res.data : t));
      setEditingTemplate(null);
    } catch { setAlertMsg('Erro ao salvar template.'); }
  };

  // ── Program treino helpers ──
  const addProgramDay = () => setProgramForm(f => ({ ...f, days: [...f.days, emptyProgramTreino()] }));
  const removeProgramDay = i => setProgramForm(f => ({ ...f, days: f.days.filter((_, j) => j !== i) }));
  const setProgramDay = (i, field, val) => setProgramForm(f => ({ ...f, days: f.days.map((d, j) => j === i ? { ...d, [field]: val } : d) }));
  const setProgramDayEx = (di, ei, field, val) => setProgramForm(f => ({ ...f, days: f.days.map((d, j) => j === di ? { ...d, exercises: d.exercises.map((e, k) => k === ei ? { ...e, [field]: val } : e) } : d) }));
  const addProgramDayEx = (di) => setProgramForm(f => ({ ...f, days: f.days.map((d, j) => j === di ? { ...d, exercises: [...(d.exercises||[]), emptyExercise()] } : d) }));
  const removeProgramDayEx = (di, ei) => setProgramForm(f => ({ ...f, days: f.days.map((d, j) => j === di ? { ...d, exercises: d.exercises.filter((_, k) => k !== ei) } : d) }));
  const applyTemplateToDay = (di, tmplId) => {
    const tmpl = templates.find(t => t.id === tmplId);
    setProgramForm(f => ({ ...f, days: f.days.map((d, j) => j === di ? { ...d, workoutTemplateId: tmplId, workoutTemplateName: tmpl?.name || '', exercises: tmpl ? tmpl.exercises.map(e => ({...e})) : d.exercises } : d) }));
  };

  const addEditProgramDay = () => setEditProgramForm(f => ({ ...f, days: [...(f.days || []), emptyProgramTreino()] }));
  const removeEditProgramDay = i => setEditProgramForm(f => ({ ...f, days: f.days.filter((_, j) => j !== i) }));
  const setEditProgramDay = (i, field, val) => setEditProgramForm(f => ({ ...f, days: f.days.map((d, j) => j === i ? { ...d, [field]: val } : d) }));
  const setEditProgramDayEx = (di, ei, field, val) => setEditProgramForm(f => ({ ...f, days: f.days.map((d, j) => j === di ? { ...d, exercises: d.exercises.map((e, k) => k === ei ? { ...e, [field]: val } : e) } : d) }));
  const addEditProgramDayEx = (di) => setEditProgramForm(f => ({ ...f, days: f.days.map((d, j) => j === di ? { ...d, exercises: [...(d.exercises||[]), emptyExercise()] } : d) }));
  const removeEditProgramDayEx = (di, ei) => setEditProgramForm(f => ({ ...f, days: f.days.map((d, j) => j === di ? { ...d, exercises: d.exercises.filter((_, k) => k !== ei) } : d) }));
  const applyTemplateToEditDay = (di, tmplId) => {
    const tmpl = templates.find(t => t.id === tmplId);
    setEditProgramForm(f => ({ ...f, days: f.days.map((d, j) => j === di ? { ...d, workoutTemplateId: tmplId, workoutTemplateName: tmpl?.name || '', exercises: tmpl ? tmpl.exercises.map(e => ({...e})) : d.exercises } : d) }));
  };

  const handleAddProgram = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/workout-programs`, programForm, { headers });
      setPrograms(prev => [res.data, ...prev]);
      setProgramForm({ name: '', description: '', level: 'beginner', days: [] });
      setShowAddProgram(false);
    } catch (err) { setAlertMsg(err.response?.data?.detail || 'Erro ao criar periodização.'); }
    finally { setSaving(false); }
  };

  const handleDeleteProgram = (id) => {
    setConfirm({
      message: 'Remover esta periodização?',
      onConfirm: async () => {
        try {
          await axios.delete(`${BACKEND_URL}/api/admin/workout-programs/${id}`, { headers });
          setPrograms(prev => prev.filter(p => p.id !== id));
        } catch { setAlertMsg('Erro ao remover.'); }
      },
    });
  };

  const startEditProgram = (prog) => {
    setEditingProgram(prog.id);
    setEditProgramForm({ ...prog, days: prog.days ? [...prog.days] : [] });
    setExpandedProgram(prog.id);
  };

  const saveEditProgram = async (id) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/workout-programs/${id}`, editProgramForm, { headers });
      setPrograms(prev => prev.map(p => p.id === id ? res.data : p));
      setEditingProgram(null);
    } catch { setAlertMsg('Erro ao salvar periodização.'); }
  };

  const categoryLabel = (val) => CATEGORIES.find(c => c.value === val)?.label || val;
  const levelLabel = (val) => LEVELS.find(l => l.value === val)?.label || val;

  const renderExercisesForm = (exercises, setEx, addEx, removeEx) => (
    <div className="flex flex-col gap-3 mt-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#555] uppercase tracking-widest">Exercícios</span>
        <button type="button" onClick={addEx}
          className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-white border border-white/10 px-2 py-1 transition-colors">
          <Plus size={11} /> Exercício
        </button>
      </div>
      {exercises.map((ex, i) => (
        <div key={i} className="bg-[#0A0A0A] border border-white/5 p-3">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-[#555]">#{i + 1}</span>
            {exercises.length > 1 && (
              <button type="button" onClick={() => removeEx(i)} className="text-[#555] hover:text-red-400"><Trash2 size={12} /></button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="col-span-2 sm:col-span-3">
              <label className={labelCls}>Nome</label>
              <input value={ex.name} onChange={e => setEx(i, 'name', e.target.value)} className={inputCls} placeholder="Supino reto" />
            </div>
            <div>
              <label className={labelCls}>Séries</label>
              <input value={ex.series} onChange={e => setEx(i, 'series', e.target.value)} className={inputCls} placeholder="4" />
            </div>
            <div>
              <label className={labelCls}>Reps</label>
              <input value={ex.reps} onChange={e => setEx(i, 'reps', e.target.value)} className={inputCls} placeholder="8-12" />
            </div>
            <div>
              <label className={labelCls}>Descanso</label>
              <input value={ex.rest} onChange={e => setEx(i, 'rest', e.target.value)} className={inputCls} placeholder="60s" />
            </div>
            <div>
              <label className={labelCls}>Tipo</label>
              <input
                list="ex-types"
                value={ex.exerciseType || 'musculação'}
                onChange={e => setEx(i, 'exerciseType', e.target.value)}
                className={inputCls}
                placeholder="musculação"
              />
              <datalist id="ex-types">
                {['Musculação','Cardio','Natação','Funcional','Luta','Esteira','Corrida','Outro'].map(t => <option key={t} value={t} />)}
              </datalist>
            </div>
            <div>
              <label className={labelCls}>Gasto kcal (sessão)</label>
              <input
                type="number"
                value={ex.estimatedCalories || ''}
                onChange={e => setEx(i, 'estimatedCalories', e.target.value)}
                className={inputCls}
                placeholder="300"
              />
            </div>
            <div className="col-span-2 sm:col-span-3">
              <label className={labelCls}>Observação</label>
              <input value={ex.notes} onChange={e => setEx(i, 'notes', e.target.value)} className={inputCls} placeholder="Opcional" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-6 lg:pt-10 pb-20">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/admin')} className="text-[#555] hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold uppercase tracking-tight">Biblioteca</h1>
            <p className="text-xs text-[#555] mt-0.5">Treinos e periodizações</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 mb-8">
          {[['templates', <Dumbbell size={14} />, 'Treinos'], ['programs', <Dumbbell size={14} />, 'Periodizações']].map(([key, icon, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium uppercase tracking-wider transition-colors border-b-2 -mb-px ${tab === key ? 'text-white border-white' : 'text-[#555] border-transparent hover:text-[#A3A3A3]'}`}
            >
              {icon} {label}
            </button>
          ))}
        </div>

        {/* ── TEMPLATES TAB ── */}
        {tab === 'templates' && (
          <div className="flex flex-col gap-6">
            <div className="flex justify-end">
              <button onClick={() => setShowAddTemplate(v => !v)}
                className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors"
              >
                <Plus size={14} /> {showAddTemplate ? 'Cancelar' : 'Novo treino'}
              </button>
            </div>

            {showAddTemplate && (
              <form onSubmit={handleAddTemplate} className="bg-[#121212] border border-white/5 p-6">
                <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4">Novo treino</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Nome</label>
                    <input required value={templateForm.name} onChange={e => setTemplateForm(f => ({...f, name: e.target.value}))} className={inputCls} placeholder="Treino A — Peito e Tríceps" />
                  </div>
                  <div>
                    <label className={labelCls}>Categoria</label>
                    <input
                      list="categories"
                      value={templateForm.category}
                      onChange={e => setTemplateForm(f => ({...f, category: e.target.value}))}
                      className={inputCls}
                      placeholder="Fullbody, ABC, custom..."
                    />
                    <datalist id="categories">
                      <option value="Fullbody" />
                      <option value="Halfbody" />
                      <option value="ABC" />
                      <option value="ABCD" />
                      <option value="Push/Pull/Legs" />
                      <option value="Superior/Inferior" />
                      <option value="Cardio" />
                      <option value="Funcional" />
                    </datalist>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Descrição</label>
                    <input value={templateForm.description} onChange={e => setTemplateForm(f => ({...f, description: e.target.value}))} className={inputCls} placeholder="Opcional" />
                  </div>
                </div>
                {renderExercisesForm(templateForm.exercises, setTplEx, addTplEx, removeTplEx)}
                <div className="mt-4">
                  <button type="submit" disabled={saving}
                    className="px-6 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Salvando...' : 'Criar treino'}
                  </button>
                </div>
              </form>
            )}

            {templates.length === 0 ? (
              <div className="text-center py-16 text-[#555] text-sm">Nenhum treino criado ainda.</div>
            ) : (
              <div className="flex flex-col gap-3">
                {templates.map(tpl => (
                  <div key={tpl.id} className="bg-[#121212] border border-white/5">
                    <div className="flex items-center justify-between px-5 py-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <button onClick={() => setExpandedTemplate(expandedTemplate === tpl.id ? null : tpl.id)}
                          className="text-[#555] hover:text-white transition-colors flex-shrink-0"
                        >
                          {expandedTemplate === tpl.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{tpl.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-[#555] border border-white/10 px-2 py-0.5">{categoryLabel(tpl.category)}</span>
                            {tpl.description && <span className="text-xs text-[#555] truncate">{tpl.description}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button onClick={() => startEditTemplate(tpl)} className="text-[#555] hover:text-white transition-colors"><Edit2 size={13} /></button>
                        <button onClick={() => handleDeleteTemplate(tpl.id)} className="text-[#555] hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                      </div>
                    </div>

                    {expandedTemplate === tpl.id && (
                      <div className="border-t border-white/5 px-5 pb-5">
                        {editingTemplate === tpl.id ? (
                          <div className="pt-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className={labelCls}>Nome</label>
                                <input value={editTemplateForm.name} onChange={e => setEditTemplateForm(f => ({...f, name: e.target.value}))} className={inputCls} />
                              </div>
                              <div>
                                <label className={labelCls}>Categoria</label>
                                <select value={editTemplateForm.category} onChange={e => setEditTemplateForm(f => ({...f, category: e.target.value}))} className={inputCls}>
                                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                              </div>
                              <div className="sm:col-span-2">
                                <label className={labelCls}>Descrição</label>
                                <input value={editTemplateForm.description || ''} onChange={e => setEditTemplateForm(f => ({...f, description: e.target.value}))} className={inputCls} />
                              </div>
                            </div>
                            {renderExercisesForm(editTemplateForm.exercises || [], setEditTplEx, addEditTplEx, removeEditTplEx)}
                            <div className="flex gap-2 mt-4">
                              <button onClick={() => saveEditTemplate(tpl.id)}
                                className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors"
                              >
                                <Check size={13} /> Salvar
                              </button>
                              <button onClick={() => setEditingTemplate(null)}
                                className="flex items-center gap-2 px-4 py-2 border border-white/10 text-sm text-[#A3A3A3] hover:text-white transition-colors"
                              >
                                <X size={13} /> Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="pt-4">
                            {(!tpl.exercises || tpl.exercises.length === 0) ? (
                              <p className="text-sm text-[#555]">Nenhum exercício neste treino.</p>
                            ) : (
                              <table className="w-full text-sm border-collapse">
                                <thead>
                                  <tr className="border-b border-white/10">
                                    <th className="text-left py-2 pr-4 text-xs text-[#555] uppercase tracking-widest font-medium">Exercício</th>
                                    <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Séries</th>
                                    <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Reps</th>
                                    <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Descanso</th>
                                    <th className="text-left py-2 pl-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden md:table-cell">Obs</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {tpl.exercises.map((ex, i) => (
                                    <tr key={i} className="border-b border-white/5">
                                      <td className="py-2 pr-4 font-medium">{ex.name}</td>
                                      <td className="py-2 px-2 text-center text-[#A3A3A3]">{ex.series || '—'}</td>
                                      <td className="py-2 px-2 text-center text-[#A3A3A3]">{ex.reps || '—'}</td>
                                      <td className="py-2 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{ex.rest || '—'}</td>
                                      <td className="py-2 pl-2 text-[#555] hidden md:table-cell">{ex.notes || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── PROGRAMS TAB ── */}
        {tab === 'programs' && (
          <div className="flex flex-col gap-6">
            <div className="flex justify-end">
              <button onClick={() => setShowAddProgram(v => !v)}
                className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors"
              >
                <Plus size={14} /> {showAddProgram ? 'Cancelar' : 'Nova periodização'}
              </button>
            </div>

            {showAddProgram && (
              <form onSubmit={handleAddProgram} className="bg-[#121212] border border-white/5 p-6">
                <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4">Nova periodização</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelCls}>Nome</label>
                    <input required value={programForm.name} onChange={e => setProgramForm(f => ({...f, name: e.target.value}))} className={inputCls} placeholder="Periodização Hipertrofia 12 semanas" />
                  </div>
                  <div>
                    <label className={labelCls}>Nível</label>
                    <select value={programForm.level} onChange={e => setProgramForm(f => ({...f, level: e.target.value}))} className={inputCls}>
                      {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Descrição</label>
                    <input value={programForm.description} onChange={e => setProgramForm(f => ({...f, description: e.target.value}))} className={inputCls} placeholder="Opcional" />
                  </div>
                </div>

                {/* Treinos builder */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#555] uppercase tracking-widest">Treinos</span>
                    <button type="button" onClick={addProgramDay}
                      className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-white border border-white/10 px-2 py-1 transition-colors">
                      <Plus size={11} /> Treino
                    </button>
                  </div>
                  {programForm.days.map((day, di) => (
                    <div key={di} className="bg-[#0A0A0A] border border-white/5 p-3 flex flex-col gap-3">
                      <div className="grid grid-cols-3 gap-2 items-end">
                        <div>
                          <label className={labelCls}>Dia da semana</label>
                          <select value={day.weekday} onChange={e => setProgramDay(di, 'weekday', e.target.value)} className={inputCls}>
                            {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>Turno</label>
                          <select value={day.timeOfDay} onChange={e => setProgramDay(di, 'timeOfDay', e.target.value)} className={inputCls}>
                            {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <label className={labelCls}>Treino pronto</label>
                            <select value={day.workoutTemplateId} onChange={e => applyTemplateToDay(di, e.target.value)} className={inputCls}>
                              <option value="">Manual / nenhum</option>
                              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                          </div>
                          <button type="button" onClick={() => removeProgramDay(di)} className="text-[#555] hover:text-red-400 transition-colors pb-2"><Trash2 size={13} /></button>
                        </div>
                      </div>
                      {/* Exercises */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#555]">Exercícios ({(day.exercises||[]).length})</span>
                          <button type="button" onClick={() => addProgramDayEx(di)} className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/5 px-2 py-0.5 transition-colors"><Plus size={10}/> Exercício</button>
                        </div>
                        {(day.exercises||[]).map((ex, ei) => (
                          <div key={ei} className="bg-[#121212] border border-white/5 p-2 grid grid-cols-4 gap-2 items-end">
                            <div className="col-span-4">
                              <input value={ex.name} onChange={e => setProgramDayEx(di, ei, 'name', e.target.value)} placeholder="Nome do exercício" className={inputCls} />
                            </div>
                            <div><label className={labelCls}>Séries</label><input value={ex.series||''} onChange={e => setProgramDayEx(di, ei, 'series', e.target.value)} className={inputCls} placeholder="4"/></div>
                            <div><label className={labelCls}>Reps</label><input value={ex.reps||''} onChange={e => setProgramDayEx(di, ei, 'reps', e.target.value)} className={inputCls} placeholder="10"/></div>
                            <div><label className={labelCls}>Descanso</label><input value={ex.rest||''} onChange={e => setProgramDayEx(di, ei, 'rest', e.target.value)} className={inputCls} placeholder="60s"/></div>
                            <div className="flex items-end"><button type="button" onClick={() => removeProgramDayEx(di, ei)} className="text-[#555] hover:text-red-400 pb-2"><Trash2 size={12}/></button></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {programForm.days.length === 0 && (
                    <p className="text-xs text-[#555] py-2">Nenhum treino configurado. Clique em "+ Treino" para adicionar.</p>
                  )}
                </div>

                <div className="mt-4">
                  <button type="submit" disabled={saving}
                    className="px-6 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Salvando...' : 'Criar periodização'}
                  </button>
                </div>
              </form>
            )}

            {programs.length === 0 ? (
              <div className="text-center py-16 text-[#555] text-sm">Nenhuma periodização criada ainda.</div>
            ) : (
              <div className="flex flex-col gap-3">
                {programs.map(prog => (
                  <div key={prog.id} className="bg-[#121212] border border-white/5">
                    <div className="flex items-center justify-between px-5 py-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <button onClick={() => setExpandedProgram(expandedProgram === prog.id ? null : prog.id)}
                          className="text-[#555] hover:text-white transition-colors flex-shrink-0">
                          {expandedProgram === prog.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{prog.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-[#555] border border-white/10 px-2 py-0.5">{levelLabel(prog.level)}</span>
                            {prog.description && <span className="text-xs text-[#555] truncate">{prog.description}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button onClick={() => startEditProgram(prog)} className="text-[#555] hover:text-white transition-colors"><Edit2 size={13} /></button>
                        <button onClick={() => handleDeleteProgram(prog.id)} className="text-[#555] hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                      </div>
                    </div>

                    {expandedProgram === prog.id && (
                      <div className="border-t border-white/5 px-5 pb-5 pt-4">
                        {editingProgram === prog.id ? (
                          <div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className={labelCls}>Nome</label>
                                <input value={editProgramForm.name} onChange={e => setEditProgramForm(f => ({...f, name: e.target.value}))} className={inputCls} />
                              </div>
                              <div>
                                <label className={labelCls}>Nível</label>
                                <select value={editProgramForm.level} onChange={e => setEditProgramForm(f => ({...f, level: e.target.value}))} className={inputCls}>
                                  {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                </select>
                              </div>
                              <div className="sm:col-span-2">
                                <label className={labelCls}>Descrição</label>
                                <input value={editProgramForm.description || ''} onChange={e => setEditProgramForm(f => ({...f, description: e.target.value}))} className={inputCls} />
                              </div>
                            </div>

                            <div className="flex flex-col gap-3 mb-4">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-[#555] uppercase tracking-widest">Treinos</span>
                                <button type="button" onClick={addEditProgramDay}
                                  className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-white border border-white/10 px-2 py-1 transition-colors">
                                  <Plus size={11} /> Treino
                                </button>
                              </div>
                              {(editProgramForm.days || []).map((day, di) => (
                                <div key={di} className="bg-[#0A0A0A] border border-white/5 p-3 flex flex-col gap-3">
                                  <div className="grid grid-cols-3 gap-2 items-end">
                                    <div>
                                      <label className={labelCls}>Dia da semana</label>
                                      <select value={day.weekday} onChange={e => setEditProgramDay(di, 'weekday', e.target.value)} className={inputCls}>
                                        {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                                      </select>
                                    </div>
                                    <div>
                                      <label className={labelCls}>Turno</label>
                                      <select value={day.timeOfDay} onChange={e => setEditProgramDay(di, 'timeOfDay', e.target.value)} className={inputCls}>
                                        {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                                      </select>
                                    </div>
                                    <div className="flex items-end gap-2">
                                      <div className="flex-1">
                                        <label className={labelCls}>Treino pronto</label>
                                        <select value={day.workoutTemplateId} onChange={e => applyTemplateToEditDay(di, e.target.value)} className={inputCls}>
                                          <option value="">Manual / nenhum</option>
                                          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </select>
                                      </div>
                                      <button type="button" onClick={() => removeEditProgramDay(di)} className="text-[#555] hover:text-red-400 transition-colors pb-2"><Trash2 size={13} /></button>
                                    </div>
                                  </div>
                                  <div className="flex flex-col gap-2">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs text-[#555]">Exercícios ({(day.exercises||[]).length})</span>
                                      <button type="button" onClick={() => addEditProgramDayEx(di)} className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/5 px-2 py-0.5 transition-colors"><Plus size={10}/> Exercício</button>
                                    </div>
                                    {(day.exercises||[]).map((ex, ei) => (
                                      <div key={ei} className="bg-[#121212] border border-white/5 p-2 grid grid-cols-4 gap-2 items-end">
                                        <div className="col-span-4">
                                          <input value={ex.name} onChange={e => setEditProgramDayEx(di, ei, 'name', e.target.value)} placeholder="Nome do exercício" className={inputCls} />
                                        </div>
                                        <div><label className={labelCls}>Séries</label><input value={ex.series||''} onChange={e => setEditProgramDayEx(di, ei, 'series', e.target.value)} className={inputCls} placeholder="4"/></div>
                                        <div><label className={labelCls}>Reps</label><input value={ex.reps||''} onChange={e => setEditProgramDayEx(di, ei, 'reps', e.target.value)} className={inputCls} placeholder="10"/></div>
                                        <div><label className={labelCls}>Descanso</label><input value={ex.rest||''} onChange={e => setEditProgramDayEx(di, ei, 'rest', e.target.value)} className={inputCls} placeholder="60s"/></div>
                                        <div className="flex items-end"><button type="button" onClick={() => removeEditProgramDayEx(di, ei)} className="text-[#555] hover:text-red-400 pb-2"><Trash2 size={12}/></button></div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="flex gap-2">
                              <button onClick={() => saveEditProgram(prog.id)}
                                className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors">
                                <Check size={13} /> Salvar
                              </button>
                              <button onClick={() => setEditingProgram(null)}
                                className="flex items-center gap-2 px-4 py-2 border border-white/10 text-sm text-[#A3A3A3] hover:text-white transition-colors">
                                <X size={13} /> Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            {(!prog.days || prog.days.length === 0) ? (
                              <p className="text-sm text-[#555]">Nenhum treino configurado nesta periodização.</p>
                            ) : (
                              <div className="flex flex-col gap-3">
                                {prog.days.map((day, di) => (
                                  <div key={di} className="bg-[#0A0A0A] border border-white/5 p-3">
                                    <div className="flex items-center gap-3 mb-2">
                                      <span className="text-xs border border-white/10 px-2 py-0.5 text-[#A3A3A3] min-w-[70px] text-center">{day.weekday}</span>
                                      <span className="text-sm font-medium flex-1">{day.workoutTemplateName || <span className="text-[#555]">Manual</span>}</span>
                                      <span className="text-xs text-[#555]">{day.timeOfDay}</span>
                                    </div>
                                    {(day.exercises||[]).length > 0 && (
                                      <table className="w-full text-xs border-collapse mt-1">
                                        <tbody>
                                          {day.exercises.map((ex, ei) => (
                                            <tr key={ei} className="border-b border-white/5">
                                              <td className="py-1 pr-3 text-[#A3A3A3]">{ex.name}</td>
                                              <td className="py-1 px-2 text-[#555] text-center">{ex.series && `${ex.series}x`}{ex.reps}</td>
                                              <td className="py-1 pl-2 text-[#555] text-right">{ex.rest}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {alertMsg && (
        <div className="fixed top-6 right-6 z-50 bg-[#121212] border border-red-500/30 text-red-400 text-sm px-4 py-3 flex items-center gap-3 shadow-xl">
          {alertMsg}
          <button onClick={() => setAlertMsg('')} className="text-[#555] hover:text-white ml-2">✕</button>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm(null)} />
          <div className="relative bg-[#121212] border border-white/10 p-6 w-full max-w-sm">
            <p className="text-sm text-[#E5E5E5] mb-6 leading-relaxed">{confirm.message}</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirm(null)}
                className="px-4 py-2 text-sm text-[#555] hover:text-white transition-colors">
                Cancelar
              </button>
              <button onClick={() => { confirm.onConfirm(); setConfirm(null); }}
                className="px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20 transition-colors">
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
